import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mic, ArrowLeft, Mail, Loader2, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSent(true);
      setEmailEnabled(response.data.email_enabled !== false);
      toast.success('Reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#1F2A37] flex flex-col">
        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center px-6 pt-10 pb-6">
          {/* Microphone Visual Element */}
          <div className="relative mb-5">
            <div className="absolute inset-0 w-20 h-20 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div className="absolute w-20 h-20 border-2 border-orange-500/30 rounded-full"></div>
              <div className="absolute w-16 h-16 border-2 border-orange-500/40 rounded-full"></div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          {/* Brand */}
          <h1 className="text-3xl font-bold text-white tracking-tight mb-1">2Tap</h1>
          <p className="text-lg text-orange-400 font-medium">Password Reset</p>
        </div>

        {/* Content Card */}
        <div className="flex-1 bg-white rounded-t-3xl shadow-2xl px-6 pt-8 pb-8">
          <div className="w-full max-w-sm mx-auto">
            {/* Success Icon */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Check Your Email</h2>
              <p className="text-slate-600 text-sm">
                If an account exists for <strong className="text-slate-800">{email}</strong>, we've sent password reset instructions.
              </p>
            </div>
            
            {/* Demo mode notice - only show if email is NOT enabled */}
            {!emailEnabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-semibold">Demo Mode</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Password reset emails are simulated. For demo accounts, use:
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• <strong>tradie@demo.com</strong>: demo123</li>
                      <li>• <strong>admin@demo.com</strong>: admin123</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Email sent notice - show when email IS enabled */}
            {emailEnabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-semibold">Check your inbox</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Click the link in the email to reset your password. The link expires in 1 hour.
                    </p>
                    <p className="text-sm text-blue-600 mt-2">
                      Don't see it? Check your spam folder.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="back-to-login-btn"
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-[#1F2A37] hover:bg-slate-800 text-white font-semibold rounded-xl"
            >
              Back to Sign In
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F2A37] flex flex-col">
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center px-6 pt-10 pb-6">
        {/* Microphone Visual Element */}
        <div className="relative mb-5">
          <div className="absolute inset-0 w-20 h-20 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative w-20 h-20 flex items-center justify-center">
            <div className="absolute w-20 h-20 border-2 border-orange-500/30 rounded-full"></div>
            <div className="absolute w-16 h-16 border-2 border-orange-500/40 rounded-full"></div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
        </div>

        {/* Brand */}
        <h1 className="text-3xl font-bold text-white tracking-tight mb-1">2Tap</h1>
        <p className="text-lg text-orange-400 font-medium">Password Reset</p>
      </div>

      {/* Form Card */}
      <div className="flex-1 bg-white rounded-t-3xl shadow-2xl px-6 pt-6 pb-8">
        <div className="w-full max-w-sm mx-auto">
          {/* Back Link & Title */}
          <div className="flex items-center gap-3 mb-6">
            <Link 
              to="/login" 
              className="p-2 -ml-2 hover:bg-slate-100 rounded-xl transition-colors"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Forgot Password</h2>
              <p className="text-sm text-slate-500">We'll send you reset instructions</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium text-sm">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 pl-10 bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              data-testid="send-reset-btn"
              className="w-full h-12 bg-[#1F2A37] hover:bg-slate-800 text-white font-semibold rounded-xl"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Send Reset Instructions
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-orange-600 hover:text-orange-700 font-semibold"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
